import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Chip,
  CircularProgress,
  withStyles,
  Card,
  ListItem,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Add as AddIcon } from "@material-ui/icons";
import { useParams } from "react-router-dom";
import { Instance } from "../../../Config";
import OwnerHeader from "../Components/OwnerHeader";
import GlobalContext from "../../../Context/globalContext";
import { SHOW_SNACKBAR } from "../../../Context/globalActions.types";
import { Skeleton } from "@material-ui/lab";

const useStyles = makeStyles((theme) => ({
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    maxHeight: 600,
  },
}));

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#3f51b5",
    color: theme.palette.common.white,
    fontWeight: 600,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:hover": {
      backgroundColor: "#F3F3F3",
      transform: "scale(1.0019)",
    },
  },
}))(TableRow);

const ViewLanguages = () => {
  const { mobileNo } = useParams();
  const { dispatch } = useContext(GlobalContext);
  const [languages, setLanguages] = useState([]);
  const [allLanguages, setAllLanguages] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openAddLangDialog, setOpenAddLangDialog] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  // New language form
  const [newLanguage, setNewLanguage] = useState({
    lang_code: "",
    lang_name: "",
    native_name: "",
  });

  // Enable language form
  const [enableForm, setEnableForm] = useState({
    lang_code: "",
    is_default: false,
    display_order: 0,
  });

  useEffect(() => {
    fetchLanguages();
    fetchAllLanguages();
  }, []);

  const fetchLanguages = async () => {
    setDataLoading(true);
    try {
      const response = await Instance.get(`/languages?mobileNo=${mobileNo}`);
      setLanguages(response.data.result);
    } catch (error) {
      console.error("Error fetching languages:", error);
      dispatch({
        type: SHOW_SNACKBAR,
        payload: {
          message: "Error fetching languages",
          type: "error",
        },
      });
    } finally {
      setDataLoading(false);
    }
  };

  const fetchAllLanguages = async () => {
    setDataLoading(true);
    try {
      const response = await Instance.get(`/languages/all`);
      setAllLanguages(response.data);
    } catch (error) {
      console.error("Error fetching all languages:", error);
      dispatch({
        type: SHOW_SNACKBAR,
        payload: {
          message: "Error fetching all languages",
          type: "error",
        },
      });
    } finally {
      setDataLoading(false);
    }
  };

  const handleEnableLanguage = async () => {
    setLoading(true);
    try {
      await Instance.post(`/languages/enable`, {
        ...enableForm,
      });
      setOpenDialog(false);
      setSuccessMessage("Language enabled successfully!");
      setOpenSuccessDialog(true);
      fetchLanguages();
      setEnableForm({ lang_code: "", is_default: false, display_order: 0 });
    } catch (error) {
      console.error("Error enabling language:", error);
      setErrorMessage(
        error.response?.data?.message || "Error enabling language"
      );
      setOpenErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewLanguage = async () => {
    setLoading(true);
    try {
      await Instance.post(`/languages/add`, newLanguage);
      setOpenAddLangDialog(false);
      setSuccessMessage("Language added successfully!");
      setOpenSuccessDialog(true);
      fetchAllLanguages();
      setNewLanguage({ lang_code: "", lang_name: "", native_name: "" });
    } catch (error) {
      console.error("Error adding language:", error);
      setErrorMessage(
        error.response?.data?.message ||
          "Error adding language. It may already exist."
      );
      setOpenErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (lang_code) => {
    setConfirmAction({
      action: "setDefault",
      lang_code,
      message: "Set this as the default language?",
    });
    setOpenConfirmDialog(true);
  };

  const handleDisableLanguage = async (lang_code) => {
    setConfirmAction({
      action: "disable",
      lang_code,
      message: "Disable this language?",
    });
    setOpenConfirmDialog(true);
  };

  const executeConfirmAction = async () => {
    setOpenConfirmDialog(false);
    setLoading(true);
    try {
      if (confirmAction.action === "setDefault") {
        await Instance.post(`/languages/set-default`, {
          lang_code: confirmAction.lang_code,
        });
        setSuccessMessage("Default language updated successfully!");
      } else if (confirmAction.action === "disable") {
        await Instance.post(`/languages/disable`, {
          lang_code: confirmAction.lang_code,
        });
        setSuccessMessage("Language disabled successfully!");
      }
      setOpenSuccessDialog(true);
      fetchLanguages();
    } catch (error) {
      console.error("Error:", error);
      setErrorMessage(error.response?.data?.message || "An error occurred");
      setOpenErrorDialog(true);
    } finally {
      setLoading(false);
      setConfirmAction(null);
    }
  };

  return (
    <>
      <OwnerHeader>
        <div className="md:flex items-center justify-between px-3 md:px-4 py-3">
          <div>
            <div className="text-indigo-500 font-semibold text-sm md:text-base">
              Translation Management
            </div>
            <div className="text-xl md:text-2xl font-extrabold leading-7 sm:leading-10 truncate mt-1">
              Language Management
            </div>
          </div>
          <div className="flex items-center mb-3 mt-2 md:mt-0 gap-2">
            <Button
              variant="contained"
              color="secondary"
              disableElevation
              startIcon={<AddIcon />}
              onClick={() => setOpenAddLangDialog(true)}
              className="bg-red-600"
            >
              Add New Language
            </Button>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
              className="bg-[#3333cc]"
            >
              Enable Language
            </Button>
          </div>
        </div>

        <div className="py-1 min-h-screen bg-[#F1F5F9] px-2">
          <Card>
            <ListItem className="pb-0">
              <Typography
                variant="h6"
                className="text-indigo-500 uppercase font-bold tracking-wider text-xs"
              >
                Language Settings
              </Typography>
            </ListItem>
            <ListItem className="pt-0">
              <Typography
                variant="h6"
                className="text-gray-500 uppercase font-bold tracking-wider text-[9px]"
              >
                Manage languages available for your application
              </Typography>
            </ListItem>
          </Card>

          <React.Fragment>
            <TableContainer
              style={{
                overflowX: "clip",
              }}
              component={Paper}
              className="mt-4"
            >
              <div className="md:flex justify-between w-full my-3 px-2 pt-3">
                <Typography className="md:ml-2 text-md md:text-lg mb-2 font-medium">
                  Enabled Languages
                </Typography>
              </div>

              <div className="max-h-screen overflow-x-hidden">
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Language Code</StyledTableCell>
                      <StyledTableCell>Language Name</StyledTableCell>
                      <StyledTableCell>Native Name</StyledTableCell>
                      <StyledTableCell>Status</StyledTableCell>
                      <StyledTableCell>Default</StyledTableCell>
                      <StyledTableCell>Display Order</StyledTableCell>
                      <StyledTableCell>Actions</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dataLoading && languages.length === 0 ? (
                      Array.from(new Array(3)).map((_, index) => (
                        <StyledTableRow key={index}>
                          <StyledTableCell>
                            <Skeleton />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Skeleton />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Skeleton />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Skeleton />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Skeleton />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Skeleton />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Skeleton />
                          </StyledTableCell>
                        </StyledTableRow>
                      ))
                    ) : !dataLoading && languages.length === 0 ? (
                      <StyledTableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="textSecondary">
                            No languages enabled
                          </Typography>
                        </TableCell>
                      </StyledTableRow>
                    ) : (
                      languages.map((lang) => (
                        <StyledTableRow key={lang.lang_code}>
                          <TableCell>{lang.lang_code}</TableCell>
                          <TableCell>{lang.lang_name}</TableCell>
                          <TableCell>{lang.native_name}</TableCell>
                          <TableCell>
                            <Chip
                              label="Enabled"
                              color="primary"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {lang.is_default ? (
                              <Chip
                                label="Default"
                                color="secondary"
                                size="small"
                              />
                            ) : (
                              <Button
                                size="small"
                                variant="text"
                                color="primary"
                                onClick={() => handleSetDefault(lang.lang_code)}
                              >
                                Set as Default
                              </Button>
                            )}
                          </TableCell>
                          <TableCell>{lang.display_order}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="text"
                              color="secondary"
                              onClick={() =>
                                handleDisableLanguage(lang.lang_code)
                              }
                              disabled={lang.is_default}
                            >
                              Disable
                            </Button>
                          </TableCell>
                        </StyledTableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TableContainer>
          </React.Fragment>

          {/* Enable Language Dialog */}
          <Dialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Enable Language for Your Company</DialogTitle>
            <DialogContent>
              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel>Select Language</InputLabel>
                <Select
                  value={enableForm.lang_code}
                  onChange={(e) =>
                    setEnableForm({ ...enableForm, lang_code: e.target.value })
                  }
                  label="Select Language"
                >
                  <MenuItem value="">
                    <em>Select a language</em>
                  </MenuItem>
                  {allLanguages.map((lang) => (
                    <MenuItem key={lang.lang_code} value={lang.lang_code}>
                      {lang.lang_name} ({lang.native_name})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={enableForm.is_default}
                    onChange={(e) =>
                      setEnableForm({
                        ...enableForm,
                        is_default: e.target.checked,
                      })
                    }
                  />
                }
                label="Set as Default Language"
              />

              <TextField
                label="Display Order"
                type="number"
                fullWidth
                margin="normal"
                variant="outlined"
                value={enableForm.display_order}
                onChange={(e) =>
                  setEnableForm({
                    ...enableForm,
                    display_order: parseInt(e.target.value),
                  })
                }
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)} color="default">
                Cancel
              </Button>
              <Button
                onClick={handleEnableLanguage}
                color="primary"
                variant="contained"
                disableElevation
                disabled={!enableForm.lang_code || loading}
                className="bg-[#3333cc]"
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} className="mr-2" /> Enabling...
                  </>
                ) : (
                  "Enable"
                )}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Add New Language Dialog */}
          <Dialog
            open={openAddLangDialog}
            onClose={() => setOpenAddLangDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Add New Language to System</DialogTitle>
            <DialogContent>
              <TextField
                label="Language Code"
                fullWidth
                margin="normal"
                variant="outlined"
                value={newLanguage.lang_code}
                onChange={(e) =>
                  setNewLanguage({
                    ...newLanguage,
                    lang_code: e.target.value.toLowerCase(),
                  })
                }
                placeholder="e.g., ta, mr, bn"
                helperText="2-3 letter language code"
              />

              <TextField
                label="Language Name (English)"
                fullWidth
                margin="normal"
                variant="outlined"
                value={newLanguage.lang_name}
                onChange={(e) =>
                  setNewLanguage({ ...newLanguage, lang_name: e.target.value })
                }
                placeholder="e.g., Tamil"
              />

              <TextField
                label="Native Name"
                fullWidth
                margin="normal"
                variant="outlined"
                value={newLanguage.native_name}
                onChange={(e) =>
                  setNewLanguage({
                    ...newLanguage,
                    native_name: e.target.value,
                  })
                }
                placeholder="e.g., தமிழ்"
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenAddLangDialog(false)}
                color="default"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddNewLanguage}
                color="primary"
                variant="contained"
                disableElevation
                disabled={
                  !newLanguage.lang_code ||
                  !newLanguage.lang_name ||
                  !newLanguage.native_name ||
                  loading
                }
                className="bg-[#3333cc]"
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} className="mr-2" /> Adding...
                  </>
                ) : (
                  "Add Language"
                )}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Confirm Dialog */}
          <Dialog
            open={openConfirmDialog}
            onClose={() => setOpenConfirmDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Confirm Action</DialogTitle>
            <DialogContent>
              <Typography>{confirmAction?.message}</Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenConfirmDialog(false)}
                color="default"
              >
                Cancel
              </Button>
              <Button
                onClick={executeConfirmAction}
                color="primary"
                variant="contained"
                disableElevation
                className="bg-[#3333cc]"
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>

          {/* Success Dialog */}
          <Dialog
            open={openSuccessDialog}
            onClose={() => setOpenSuccessDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Success</DialogTitle>
            <DialogContent>
              <Typography>{successMessage}</Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenSuccessDialog(false)}
                color="primary"
                variant="contained"
                disableElevation
                className="bg-[#3333cc]"
              >
                OK
              </Button>
            </DialogActions>
          </Dialog>

          {/* Error Dialog */}
          <Dialog
            open={openErrorDialog}
            onClose={() => setOpenErrorDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Error</DialogTitle>
            <DialogContent>
              <Typography>{errorMessage}</Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenErrorDialog(false)}
                color="secondary"
                variant="contained"
                disableElevation
              >
                OK
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </OwnerHeader>
    </>
  );
};

export default ViewLanguages;
